import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  Download,
  Calendar,
  Filter,
  DollarSign,
  Package,
  Clock,
  Target,
  FileText,
  Eye,
  RefreshCw,
  Edit3
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency, formatDate } from '@/utils'
import { apiClient, projectsApi, materialsApi, reportsApi } from '@/services/api'

// Import refactored components
import { BusinessOverviewReport } from './components/BusinessOverviewReport'
import { InventoryAnalysisReport } from './components/InventoryAnalysisReport'
import { StockAdjustmentsReport } from './components/StockAdjustmentsReport'
import { 
  ProjectsReport, 
  TimeReport, 
  FinancialReport, 
  TaxReport 
} from './components/PlaceholderReports'

export const ReportsPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const [selectedReport, setSelectedReport] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [exportLoading, setExportLoading] = useState(false)

  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ]

  const reportTypes = [
    { value: 'overview', label: 'Business Overview', icon: BarChart3 },
    { value: 'inventory', label: 'Inventory Analysis', icon: Package },
    { value: 'projects', label: 'Project Performance', icon: Target },
    { value: 'time', label: 'Time & Labor', icon: Clock },
    { value: 'financial', label: 'Financial Summary', icon: DollarSign },
    { value: 'stock-adjustments', label: 'Stock Adjustments', icon: Edit3 },
    { value: 'tax', label: 'Tax Reports', icon: FileText }
  ]

  const exportReport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      let endpoint = ''
      switch (selectedReport) {
        case 'tax':
          endpoint = '/reports/export/tax'
          break
        case 'financial':
          endpoint = '/reports/export/quickbooks'
          break
        default:
          // For other reports, use the general export endpoint
          endpoint = '/reports/analytics/summary'
      }

      // Ensure end date includes the full current day
      const endDate = new Date()
      endDate.setHours(23, 59, 59, 999)
      
      const result = await apiClient.post(endpoint, {
        format: format.toLowerCase(),
        startDate: getStartDateForPeriod(selectedPeriod),
        endDate: endDate.toISOString(),
        includeProjects: true,
        includePurchases: true,
        includeTimeTracking: true
      })
      
      // If there's a download URL, use the authenticated API client to download the file
      if (result.downloadUrl) {
        try {
          // Use the API client to download the file with proper authentication
          const downloadResponse = await apiClient.get(result.downloadUrl, {
            responseType: 'blob'
          })
          
          // Determine the correct file extension and MIME type based on actual file content
          let fileExtension = 'json'
          let mimeType = 'application/json'
          
          if (result.filePath) {
            // Extract actual file extension from the backend file path
            const pathExtension = result.filePath.split('.').pop()?.toLowerCase()
            if (pathExtension === 'csv') {
              fileExtension = 'csv'
              mimeType = 'text/csv'
            } else if (pathExtension === 'json') {
              fileExtension = 'json'
              mimeType = 'application/json'
            } else if (pathExtension === 'pdf') {
              fileExtension = 'pdf'
              mimeType = 'application/pdf'
            } else if (pathExtension === 'xlsx') {
              fileExtension = 'xlsx'
              mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
          }
          
          // Create a blob URL and trigger download
          const blob = new Blob([downloadResponse], { type: mimeType })
          const url = window.URL.createObjectURL(blob)
          
          // Create a temporary link element to trigger download
          const link = document.createElement('a')
          link.href = url
          // Use the actual file extension, not the requested format
          link.download = result.fileName || `report_${selectedReport}_${Date.now()}.${fileExtension}`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(url)
          
          alert('Export completed successfully!')
        } catch (downloadError) {
          console.error('Error downloading file:', downloadError)
          alert('Export completed but download failed. Please try again.')
        }
      } else {
        alert('Export completed successfully!')
      }
    } catch (error) {
      console.error('Error exporting report:', error)
      alert('Export failed. Please try again.')
    }
  }

  const handleQuickBooksExport = async (format: 'json' | 'csv' = 'csv') => {
    setExportLoading(true)
    try {
      console.log(`🚀 Starting QuickBooks export in ${format.toUpperCase()} format`)
      
      await reportsApi.exportQuickBooks({
        format,
        includeProjects: true,
        includePurchases: true,
        includeTimeTracking: true,
        startDate: getStartDateForPeriod(selectedPeriod),
        endDate: new Date().toISOString()
      })
      
      console.log('✅ QuickBooks export completed successfully')
    } catch (error) {
      console.error('❌ QuickBooks export failed:', error)
      alert('QuickBooks export failed. Please try again.')
    } finally {
      setExportLoading(false)
    }
  }

  const getStartDateForPeriod = (period: string) => {
    const now = new Date()
    switch (period) {
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString()
      case 'month':
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      case 'quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
        return quarterStart.toISOString()
      case 'year':
        return new Date(now.getFullYear(), 0, 1).toISOString()
      default:
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString()
    }
  }

  const refreshData = () => {
    setLoading(true)
    // Trigger refresh in child components by changing a key or using a callback
    setTimeout(() => setLoading(false), 1000)
  }

  const renderReportContent = () => {
    switch (selectedReport) {
      case 'overview': 
        return <BusinessOverviewReport selectedPeriod={selectedPeriod} />
      case 'inventory': 
        return <InventoryAnalysisReport />
      case 'projects': 
        return <ProjectsReport />
      case 'time': 
        return <TimeReport />
      case 'financial': 
        return <FinancialReport />
      case 'stock-adjustments': 
        return <StockAdjustmentsReport />
      case 'tax': 
        return <TaxReport />
      default: 
        return <BusinessOverviewReport selectedPeriod={selectedPeriod} />
    }
  }

  return (
    <>
      <Helmet>
        <title>Reports & Analytics - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Reports & Analytics
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Comprehensive business insights and performance analytics
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={refreshData}
              leftIcon={<RefreshCw className="w-4 h-4" />}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
            <Button
              onClick={() => exportReport('pdf')}
              leftIcon={<Download className="w-4 h-4" />}
            >
              Export Report
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Report Type */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Report Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {reportTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedReport(type.value)}
                    className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                      selectedReport === type.value
                        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    <type.icon className={`w-4 h-4 mx-auto mb-1 ${
                      selectedReport === type.value ? 'text-white' : ''
                    }`} />
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Period Filter */}
            <div className="w-full lg:w-48">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time Period
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              >
                {periodOptions.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Export Options */}
            <div className="w-full lg:w-48">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Export Format
              </label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportReport('pdf')}
                  className="flex-1"
                >
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportReport('excel')}
                  className="flex-1"
                >
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportReport('csv')}
                  className="flex-1"
                >
                  CSV
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Report Content */}
        {renderReportContent()}

        {/* QuickBooks Export Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                QuickBooks Integration
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Export your business data in QuickBooks-compatible format for seamless accounting integration
              </p>
            </div>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">What gets exported:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700 dark:text-blue-300">
              <div>
                <strong>Customers:</strong> Project customer names and contact information
              </div>
              <div>
                <strong>Items/Products:</strong> Project deliverables and inventory items
              </div>
              <div>
                <strong>Invoices:</strong> Completed projects with pricing and dates
              </div>
              <div>
                <strong>Expenses:</strong> Material purchases and supplier transactions
              </div>
              <div>
                <strong>Time Activities:</strong> Labor hours and time tracking entries
              </div>
              <div>
                <strong>Inventory:</strong> Current stock levels and valuations
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleQuickBooksExport('csv')}
              leftIcon={<Download className="w-4 h-4" />}
              loading={exportLoading}
              disabled={exportLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Export CSV for QuickBooks
            </Button>
            <Button
              variant="outline"
              onClick={() => handleQuickBooksExport('json')}
              leftIcon={<Download className="w-4 h-4" />}
              loading={exportLoading}
              disabled={exportLoading}
              className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              Export JSON
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Button
              variant="outline"
              onClick={() => exportReport('pdf')}
              leftIcon={<FileText className="w-4 h-4" />}
              className="justify-start"
            >
              Generate Tax Report
            </Button>
            <Button
              variant="outline"
              onClick={() => exportReport('excel')}
              leftIcon={<BarChart3 className="w-4 h-4" />}
              className="justify-start"
            >
              Profit Analysis
            </Button>
            <Button
              variant="outline"
              onClick={() => exportReport('csv')}
              leftIcon={<Package className="w-4 h-4" />}
              className="justify-start"
            >
              Inventory Valuation
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/reports/stock-adjustments'}
              leftIcon={<Edit3 className="w-4 h-4" />}
              className="justify-start"
            >
              Stock Adjustments
            </Button>
            <Button
              variant="outline"
              onClick={() => exportReport('pdf')}
              leftIcon={<Clock className="w-4 h-4" />}
              className="justify-start"
            >
              Time Summary
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}